import { useApolloClient } from '@vue/apollo-composable'
import { type ComputedRef, ref } from 'vue'

import { email } from '@/lib/validation'
import UPDATE_COMPANY_AMBASSADOR from '@/modules/company/queries/UpdateAmbassador.gql'
import UPDATE_COMPANY from '@/modules/company/queries/UpdateCompany.gql'
import UPDATE_COMPANY_CONTACT_PERSON from '@/modules/company/queries/UpdateCompanyContactPerson.gql'
import UPDATE_COMPANY_THEME from '@/modules/company/queries/UpdateCompanyTheme.gql'
import UPDATE_ONBOARDING_SLOGAN from '@/modules/company/queries/UpdateSlogan.gql'
import UPDATE_COMPANY_META from '@/modules/settings/companySettings/queries/UpdateCompanyMetaData.gql'

import { isCommissionInputValid } from '../lib/onboardingCommission'
import {
  buildAmbassadorInput,
  buildCompanyCoreInput,
  buildGeneralContactInput,
  buildMetaInput,
  buildSloganValue,
  buildThemeColors,
  type CompanyData,
} from '../lib/onboardingMutationInputs'
import { isValidOnboardingPhone } from '../lib/onboardingPhoneValidation'
import type { OnboardingFormData } from '../lib/onboardingPrefill'

/**
 * Per-field autosave for the onboarding data-entry / review forms.
 *
 * Previously nothing was written until the final "Complete" on the Review step,
 * so a page reload lost every value the user had typed. This persists edits as
 * the user goes (debounced per mutation group), reusing the exact same input
 * builders as the final submit so the two can't diverge.
 *
 * Autosave is best-effort: failures are logged, not surfaced, and are corrected
 * on the next successful edit or by the authoritative final submit.
 */
type FieldGroup = 'company-core' | 'theme' | 'slogan' | 'meta' | 'ambassador' | 'contact-person'

const GROUP_FIELDS: Record<FieldGroup, string[]> = {
  'company-core': [
    'name',
    'website',
    'linkImprint',
    'linkPrivacy',
    'linkAbout',
    'street',
    'city',
    'zipcode',
    'country',
    'state',
    'brokerageName',
    'licenseNumber',
    'licenseStatus',
    'brokerageNameSameAsName',
    'commission',
  ],
  theme: ['primaryColor', 'secondaryColor'],
  slogan: ['slogan'],
  meta: ['businessDescription'],
  ambassador: [
    'ambassadorSalutation',
    'ambassadorFirstName',
    'ambassadorLastName',
    'ambassadorRole',
    'ambassadorEmail',
    'phone',
    'ambassadorMobile',
  ],
  'contact-person': ['contactFirstName', 'contactLastName', 'contactEmail', 'contactPhone'],
}

const FIELD_TO_GROUP: Record<string, FieldGroup> = (() => {
  const map: Record<string, FieldGroup> = {}
  for (const group of Object.keys(GROUP_FIELDS) as FieldGroup[]) {
    for (const field of GROUP_FIELDS[group]) map[field] = group
  }
  return map
})()

const AUTOSAVE_DEBOUNCE_MS = 800
function emailOkOrEmpty(value: string | undefined): boolean {
  return email((value || '').trim()) === true
}

function phoneOkOrEmpty(value: string | undefined): boolean {
  const v = (value || '').trim()
  return !v || isValidOnboardingPhone(v)
}

export function useOnboardingFieldPersistence(options: {
  company: ComputedRef<CompanyData | null>
  formData: OnboardingFormData
  isUsOnboarding: ComputedRef<boolean>
  countryValueForSubmit: () => string | null
}) {
  const { company, formData, isUsOnboarding, countryValueForSubmit } = options
  const { resolveClient } = useApolloClient()

  const timers: Partial<Record<FieldGroup, ReturnType<typeof setTimeout>>> = {}
  const inflight: Partial<Record<FieldGroup, Promise<void>>> = {}
  const lastSavedSignature: Partial<Record<FieldGroup, string>> = {}
  // Remembers the GENERAL contact created by autosave so later edits update it
  // instead of creating a duplicate (the company record isn't refetched here).
  const createdContactId = ref<string | undefined>(undefined)

  function fieldGroupOf(field: string): FieldGroup | null {
    return FIELD_TO_GROUP[field] ?? null
  }

  function groupValidForAutosave(group: FieldGroup): boolean {
    if (group === 'company-core') {
      return isUsOnboarding.value || isCommissionInputValid(formData.commission)
    }
    if (group === 'ambassador') {
      return (
        emailOkOrEmpty(formData.ambassadorEmail) &&
        phoneOkOrEmpty(formData.phone) &&
        phoneOkOrEmpty(formData.ambassadorMobile)
      )
    }
    if (group === 'contact-person') {
      return emailOkOrEmpty(formData.contactEmail) && phoneOkOrEmpty(formData.contactPhone)
    }
    return true
  }

  /** Builds `{ mutation, variables, isContactCreate }` for a group, or null to skip. */
  function buildGroupMutation(group: FieldGroup, c: CompanyData) {
    switch (group) {
      case 'company-core':
        return {
          mutation: UPDATE_COMPANY,
          variables: {
            input: buildCompanyCoreInput(formData, c, {
              isUsOnboarding: isUsOnboarding.value,
              countryValueForSubmit,
            }),
          },
          isContactCreate: false,
        }
      case 'theme': {
        const colors = buildThemeColors(formData, c)
        if (!colors) return null
        return {
          mutation: UPDATE_COMPANY_THEME,
          variables: { input: { companyId: c.id, colors } },
          isContactCreate: false,
        }
      }
      case 'slogan': {
        const slogan = buildSloganValue(formData, c)
        if (slogan == null) return null
        return {
          mutation: UPDATE_ONBOARDING_SLOGAN,
          variables: { input: { companyId: c.id, slogan } },
          isContactCreate: false,
        }
      }
      case 'meta': {
        const input = buildMetaInput(formData, c)
        if (!input) return null
        return { mutation: UPDATE_COMPANY_META, variables: { input }, isContactCreate: false }
      }
      case 'ambassador':
        return {
          mutation: UPDATE_COMPANY_AMBASSADOR,
          variables: { input: buildAmbassadorInput(formData, c) },
          isContactCreate: false,
        }
      case 'contact-person': {
        const { input, existingId } = buildGeneralContactInput(formData, c)
        const hasValue = input.firstname || input.lastname || input.email || input.phone
        if (!hasValue) return null
        const id = existingId || createdContactId.value
        return {
          mutation: UPDATE_COMPANY_CONTACT_PERSON,
          variables: { input: id ? { id, ...input } : input },
          isContactCreate: !id,
        }
      }
    }
  }

  async function persistGroup(group: FieldGroup): Promise<void> {
    const c = company.value
    if (!c?.id) return
    if (!groupValidForAutosave(group)) return
    const built = buildGroupMutation(group, c)
    if (!built) return
    const signature = JSON.stringify(built.variables)
    if (signature === lastSavedSignature[group]) return
    try {
      const result = await resolveClient().mutate({
        mutation: built.mutation,
        variables: built.variables,
      })
      if (built.isContactCreate) {
        const newId = (result?.data as { contactPerson?: { id?: string } })?.contactPerson?.id
        if (newId) createdContactId.value = newId
      }
      lastSavedSignature[group] = signature
    } catch (err) {
      console.warn(`[Onboarding] autosave failed for "${group}"`, err)
    }
  }

  /** Serialize saves per group so two quick edits never race on the same record. */
  function runGroupSave(group: FieldGroup): Promise<void> {
    const prev = inflight[group] ?? Promise.resolve()
    const next = prev.then(() => persistGroup(group))
    inflight[group] = next.finally(() => {
      if (inflight[group] === next) inflight[group] = undefined
    })
    return next
  }

  /** Debounced autosave — call whenever the user edits `field`. */
  function scheduleAutosave(field: string): void {
    const group = fieldGroupOf(field)
    if (!group) return
    if (timers[group]) clearTimeout(timers[group])
    timers[group] = setTimeout(() => {
      timers[group] = undefined
      void runGroupSave(group)
    }, AUTOSAVE_DEBOUNCE_MS)
  }

  /** Flush any debounced/in-flight saves (e.g. before leaving the step). */
  function flushPendingAutosaves(): Promise<void> {
    const pending: Promise<void>[] = []
    for (const group of Object.keys(GROUP_FIELDS) as FieldGroup[]) {
      if (timers[group]) {
        clearTimeout(timers[group])
        timers[group] = undefined
        pending.push(runGroupSave(group))
      } else if (inflight[group]) {
        pending.push(inflight[group] as Promise<void>)
      }
    }
    return Promise.all(pending).then(() => undefined)
  }

  return {
    scheduleAutosave,
    flushPendingAutosaves,
    // Exposed so the final submit can reuse a GENERAL contact autosave created
    // here instead of creating a duplicate id-less one.
    createdContactId,
  }
}
